import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Plus, Eye, UserRound } from 'lucide-react'
import type { Child } from '@/api/client'
import { client } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

export function ChildrenPage() {
  const { t } = useTranslation('children')
  const { parent } = useAuthStore()
  const [children, setChildren] = useState<Child[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchChildren = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await client.get('/children/')
        const uniqueChildren = response.data.reduce((acc: Child[], child: Child) => {
          if (!acc.some(c => c.id === child.id)) {
            acc.push(child)
          }
          return acc
        }, [])
        setChildren(uniqueChildren || [])
      } catch (err) {
        setError(t('errors.loadFailed'))
      } finally {
        setLoading(false)
      }
    }

    if (parent?.id) {
      fetchChildren()
    } else {
      setLoading(false)
    }
  }, [parent?.id, t])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('childrenBox.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {children.length > 0 ? `${children.length} ${t('childrenBox.title').toLowerCase()}` : ''}
          </p>
        </div>
        <Button asChild>
          <Link to="/children/add">
            <Plus className="h-4 w-4 mr-1" />
            {t('childrenBox.addChild')}
          </Link>
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive font-medium">{error}</p>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : children.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {children.map((child) => (
            <Card key={child.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className={`rounded-full p-2.5 ${child.gender === 'male' ? 'bg-sky-100' : 'bg-pink-100'}`}>
                      <UserRound className={`h-6 w-6 ${child.gender === 'male' ? 'text-sky-600' : 'text-pink-600'}`} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground truncate">
                      {child.first_name} {child.last_name}
                    </h3>
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium">{t('form.registrationNumber')}:</span> {child.registration_number}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium">{t('form.birthDate')}:</span>{' '}
                        {(() => {
                          const date = new Date(child.birth_date)
                          return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`
                        })()}
                      </p>
                    </div>
                    <div className="mt-3">
                      <Button variant="outline" size="sm" asChild className="w-full">
                        <Link to={`/children/${child.id}`}>
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          {t('childrenBox.viewButton')}
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="rounded-full bg-muted p-4 mb-4">
              <UserRound className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground mb-4">{t('childrenBox.noChildren')}</p>
            <Button asChild>
              <Link to="/children/add">
                <Plus className="h-4 w-4 mr-1" />
                {t('childrenBox.addChild')}
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
